import { Module } from '@/types/courseTypes';
import { logger } from '@/utils/logger';

/**
 * Enhanced Module Cache with LRU, TTL, and Progressive Loading
 * Enables instant rendering while fresh data loads in background
 */

interface CacheEntry {
  module: Module;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
  isComplete: boolean; // true if full module, false if shell
}

interface CacheStats {
  hits: number;
  misses: number;
  size: number;
  maxSize: number;
  hitRate: number;
}

class EnhancedModuleCacheManager {
  private cache = new Map<string, CacheEntry>();
  private readonly MAX_SIZE = 50; // LRU cache size
  private readonly SHELL_TTL = 10 * 60 * 1000; // 10 minutes for shells
  private readonly FULL_TTL = 5 * 60 * 1000; // 5 minutes for full modules
  private hits = 0;
  private misses = 0;

  /**
   * Get module from cache (prioritizes fresh data but returns stale if available)
   */
  get(key: string): Module | null {
    const cached = this.cache.get(key);
    
    if (!cached) {
      this.misses++;
      logger.debug(`[ModuleCache] MISS: ${key}`);
      return null;
    }

    // Update access stats
    cached.accessCount++;
    cached.lastAccessed = Date.now();
    this.hits++;
    
    // Return even if expired - stale data is better than loading screen
    const age = Date.now() - cached.timestamp;
    const ttl = cached.isComplete ? this.FULL_TTL : this.SHELL_TTL;
    
    if (age > ttl) {
      logger.debug(`[ModuleCache] STALE HIT: ${key} (${Math.round(age / 1000)}s old)`);
    } else {
      logger.debug(`[ModuleCache] FRESH HIT: ${key} (${Math.round(age / 1000)}s old)`);
    }

    return cached.module;
  }

  /**
   * Store module shell (content only) for instant rendering
   */
  setShell(key: string, module: Module): void {
    this.set(key, module, false);
  }

  /**
   * Store complete module (with all data)
   */
  setComplete(key: string, module: Module): void {
    this.set(key, module, true);
  }

  /**
   * Internal set method
   */
  private set(key: string, module: Module, isComplete: boolean): void {
    // Enforce LRU by checking size before adding
    if (this.cache.size >= this.MAX_SIZE && !this.cache.has(key)) {
      this.evictLRU();
    }

    const existing = this.cache.get(key);
    
    this.cache.set(key, {
      module,
      timestamp: Date.now(),
      accessCount: existing?.accessCount || 0,
      lastAccessed: Date.now(),
      isComplete
    });

    const type = isComplete ? 'COMPLETE' : 'SHELL';
    logger.debug(`[ModuleCache] SET ${type}: ${key} (size: ${this.cache.size})`);
  }

  /**
   * Check if module exists in cache and is fresh
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Check if cached data is stale
   */
  isStale(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return true;

    const age = Date.now() - cached.timestamp;
    const ttl = cached.isComplete ? this.FULL_TTL : this.SHELL_TTL;
    
    return age > ttl;
  }

  /**
   * Evict least recently used entry
   */
  private evictLRU(): void {
    let lruKey: string | null = null;
    let lruTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < lruTime) {
        lruTime = entry.lastAccessed;
        lruKey = key;
      }
    }

    if (lruKey) {
      this.cache.delete(lruKey);
      logger.debug(`[ModuleCache] EVICTED LRU: ${lruKey}`);
    }
  }

  /**
   * Cleanup expired entries (called periodically)
   */
  private cleanup(): void {
    const now = Date.now();
    let cleaned = 0;

    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      const ttl = entry.isComplete ? this.FULL_TTL : this.SHELL_TTL;
      
      // Only delete if very old (2x TTL) and low access count
      if (age > ttl * 2 && entry.accessCount < 2) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      logger.debug(`[ModuleCache] CLEANUP: Removed ${cleaned} entries`);
    }
  }

  /**
   * Prefetch module for future access
   */
  async prefetch(key: string, fetchFn: () => Promise<Module | null>): Promise<void> {
    // Don't prefetch if already cached and fresh
    if (this.has(key) && !this.isStale(key)) {
      logger.debug(`[ModuleCache] SKIP PREFETCH: ${key} (already cached)`);
      return;
    }

    try {
      logger.debug(`[ModuleCache] PREFETCH: ${key}`);
      const module = await fetchFn();
      if (module) {
        this.setComplete(key, module);
      }
    } catch (error) {
      logger.warn(`[ModuleCache] PREFETCH FAILED: ${key}`, error);
    }
  }

  /**
   * Warm cache with multiple modules
   */
  async warmCache(modules: Array<{ key: string; module: Module }>): Promise<void> {
    logger.info(`[ModuleCache] WARMING: ${modules.length} modules`);
    
    modules.forEach(({ key, module }) => {
      this.setComplete(key, module);
    });
  }

  /**
   * Clear specific entry or entire cache
   */
  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
      logger.debug(`[ModuleCache] CLEARED: ${key}`);
    } else {
      this.cache.clear();
      this.hits = 0;
      this.misses = 0;
      logger.info('[ModuleCache] CLEARED ALL');
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const total = this.hits + this.misses;
    return {
      hits: this.hits,
      misses: this.misses,
      size: this.cache.size,
      maxSize: this.MAX_SIZE,
      hitRate: total > 0 ? this.hits / total : 0
    };
  }

  /**
   * Initialize periodic cleanup
   */
  startPeriodicCleanup(intervalMs: number = 60000): void {
    setInterval(() => this.cleanup(), intervalMs);
    logger.info(`[ModuleCache] Periodic cleanup enabled (${intervalMs}ms)`);
  }
}

// Export singleton instance
export const ModuleCache = new EnhancedModuleCacheManager();

// Start periodic cleanup
ModuleCache.startPeriodicCleanup();
